import {PageContainer} from '@/components/organisms';
import {ConfirmationModal} from '@/components/molecules';
import {TiersChart} from '@/containers/ClubPlus/Tabs/Overview/TiersChart/TiersChart';
import {
  getCurrencyTiersData,
  getLabelTransform
} from '@/containers/ClubPlus/Tabs/Overview/TiersChart/TiersChart.utils';
import {useTierConfigurations} from './useTierConfigurations';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';
import {Button} from '@/components/atoms';
import {Font} from '@pepsico-ds/ui';
import {TierTable} from './TierTable';
import {MarketLevelConfigurations} from './MarketLevelConfigurations';
import './_tierConfigurations.scss';

export const TierConfigurationsTab = ({autoEdit = false}) => {
  const {formatMessage} = useIntl();
  const {
    tiers,
    isEditMode,
    errors,
    hasErrors,
    isMarketConfigValid,
    hasTierConfiguration,
    lastUpdated,
    currencyCode,
    marketConfigValues,
    nextEvaluationDate,
    isSaving,
    showSaveModal,
    handleEdit,
    handleCancel,
    handleSave,
    handleConfirmSave,
    handleCancelSave,
    handleUpperRangeChange,
    setSalesMethod,
    setEvaluationFrequency,
    setSalesDays,
    setIncludeOfflineOrders,
    salesPerformanceDropdownList,
    evaluationFrequencyDropdownList
  } = useTierConfigurations(autoEdit);

  const chartData = tiers.map((tier) => ({
    name: tier.name,
    color: `#${tier.color}`,
    upperRange: tier.upperRange,
    lowerRange: tier.lowerRange
  }));

  const currencyData = getCurrencyTiersData(chartData, currencyCode);

  const currencySegments = currencyData.list.map((item) => ({
    name: item.name,
    color: item.color,
    size: item.size,
    quantityLabel: item.quantityLabel
  }));

  const currencyLabels = currencyData.boundaryLabels.map((text, index) => ({
    text,
    position: currencyData.boundaryPositions[index],
    transform: getLabelTransform(index, currencyData.boundaryLabels.length)
  }));

  const marketConfig = [
    {
      key: 'salesMethod',
      required: true,
      label: formatMessage({
        id: 'CLUB_PLUS.TIER_CONFIGURATIONS_MARKET_SALES_PERFORMANCE_LABEL'
      }),
      displayValue:
        marketConfigValues?.salesMethod ??
        formatMessage({
          id: 'CLUB_PLUS.TIER_CONFIGURATIONS_MARKET_SALES_PERFORMANCE_VALUE'
        }),
      value: marketConfigValues?.salesMethodValue ?? ''
    },
    {
      key: 'evaluationFrequency',
      required: true,
      label: formatMessage({
        id: 'CLUB_PLUS.TIER_CONFIGURATIONS_MARKET_EVALUATION_FREQUENCY_LABEL'
      }),
      displayValue: marketConfigValues?.evaluationFrequency ?? '-',
      value: marketConfigValues?.evaluationFrequencyValue ?? ''
    },
    {
      key: 'evaluationExecution',
      label: formatMessage({
        id: 'CLUB_PLUS.TIER_CONFIGURATIONS_MARKET_EVALUATION_EXECUTION_LABEL'
      }),
      displayValue: marketConfigValues?.evaluationExecution ?? '-',
      value: marketConfigValues?.evaluationFrequencyValue ?? ''
    },
    {
      key: 'salesDays',
      required: true,
      label: formatMessage({
        id: 'CLUB_PLUS.TIER_CONFIGURATIONS_MARKET_SALES_DAYS_LABEL'
      }),
      displayValue:
        marketConfigValues?.salesDays ??
        formatMessage({
          id: 'CLUB_PLUS.TIER_CONFIGURATIONS_MARKET_SALES_DAYS_VALUE'
        }),
      value: marketConfigValues?.salesDaysValue ?? ''
    },
    {
      key: 'includeOfflineOrders',
      label: formatMessage({
        id: 'CLUB_PLUS.TIER_CONFIGURATIONS_MARKET_INCLUDE_OFFLINE_LABEL'
      }),
      displayValue:
        marketConfigValues?.includeOfflineOrders ??
        formatMessage({
          id: 'CLUB_PLUS.TIER_CONFIGURATIONS_MARKET_INCLUDE_OFFLINE_VALUE'
        }),
      value: marketConfigValues?.includeOfflineOrdersValue ?? false
    }
  ];

  return (
    <div data-testid='tier-configurations-tab'>
      {hasTierConfiguration ? (
        <>
          <PageContainer>
            <Font
              variant='bold'
              size='small'
              textColorClass='text-white'
              className='mb-6 block'>
              {formatMessage({id: 'CLUB_PLUS.TIER_CONFIGURATIONS_TITLE'})}
            </Font>

            {tiers.length > 0 && (
              <TiersChart
                mode='currency'
                segments={currencySegments}
                boundaryLabels={currencyLabels}
              />
            )}

            <TierTable
              tiers={tiers}
              currencyCode={currencyCode}
              isEditMode={isEditMode}
              errors={errors}
              handleUpperRangeChange={handleUpperRangeChange}
            />
          </PageContainer>

          <PageContainer>
            <Font
              variant='bold'
              size='small'
              textColorClass='text-white'
              className='mb-6 block'>
              {formatMessage({
                id: 'CLUB_PLUS.TIER_CONFIGURATIONS_MARKET_CONFIG_TITLE'
              })}
            </Font>

            <MarketLevelConfigurations
              configItems={marketConfig}
              isEditMode={isEditMode}
              onSalesMethodChange={setSalesMethod}
              onEvaluationFrequencyChange={setEvaluationFrequency}
              onSalesDaysChange={setSalesDays}
              onIncludeOfflineOrdersChange={setIncludeOfflineOrders}
              salesMethodDropdownOptions={salesPerformanceDropdownList}
              evaluationFrequencyDropdownOptions={
                evaluationFrequencyDropdownList
              }
            />
          </PageContainer>

          <PageContainer>
            <div className='tier-footer'>
              <Font variant='regular' size='small'>
                {formatMessage(
                  {id: 'CLUB_PLUS.TIER_CONFIGURATIONS_LAST_UPDATED'},
                  {date: lastUpdated}
                )}
              </Font>

              <div className='flex gap-3'>
                {isEditMode ? (
                  <>
                    <Button
                      handleButton={handleCancel}
                      uiSchema={{
                        title: formatMessage({
                          id: 'CLUB_PLUS.TIER_CONFIGURATIONS_CANCEL_BUTTON'
                        }),
                        variant: 'tertiaryInverse',
                        size: 'medium'
                      }}
                    />
                    <Button
                      handleButton={handleSave}
                      uiSchema={{
                        title: formatMessage({
                          id: 'CLUB_PLUS.TIER_CONFIGURATIONS_SAVE_CHANGES_BUTTON'
                        }),
                        variant: 'primary',
                        size: 'medium',
                        disabled: hasErrors || isSaving || !isMarketConfigValid
                      }}
                    />
                  </>
                ) : (
                  <Button
                    handleButton={handleEdit}
                    uiSchema={{
                      title: formatMessage({
                        id: 'CLUB_PLUS.TIER_CONFIGURATIONS_EDIT_BUTTON'
                      }),
                      variant: 'primary',
                      size: 'medium',
                      iconLeading: 'edit'
                    }}
                  />
                )}
              </div>
            </div>
          </PageContainer>

          <ConfirmationModal
            showModal={showSaveModal}
            setShowModal={handleCancelSave}
            handleConfirm={handleConfirmSave}
            title={formatMessage({
              id: 'CLUB_PLUS.TIER_CONFIGURATIONS_SAVE_CONFIRM_TITLE'
            })}
            text={formatMessage(
              {
                id: 'CLUB_PLUS.TIER_CONFIGURATIONS_SAVE_CONFIRM_MESSAGE'
              },
              {nextEvaluationDate}
            )}
            primaryButtonText={formatMessage({id: 'GENERAL.YES'})}
            secondaryButtonText={formatMessage({id: 'GENERAL.NO'})}
            type='S'
            className='tier-save-confirm-modal'
          />
        </>
      ) : (
        <PageContainer>
          <Font
            variant='regular'
            size='small'
            data-testid='tier-configurations-empty-state'>
            {formatMessage({
              id: 'CLUB_PLUS.TIER_CONFIGURATIONS_EMPTY_STATE_MESSAGE'
            })}
          </Font>
        </PageContainer>
      )}
    </div>
  );
};

TierConfigurationsTab.propTypes = {
  autoEdit: PropTypes.bool
};
